import { SIMILAR_UNIT_SOURCE_LABEL } from "shared/app/SimilarUnit/constants";
import type { SimilarUnitOutreach } from "../api/crm";
import type { Message } from "../types";

const RECENT_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

/** Outbound part 2 message, recognised by its communication-service source label. */
export const isSimilarUnitNotificationMessage = (
  message: Pick<Message, "direction" | "source_label">
): boolean =>
  message.direction === "outbound" &&
  message.source_label?.trim() === SIMILAR_UNIT_SOURCE_LABEL;

/** Any part 2 message sent in the last 90 days (header badge). */
export const hasRecentSimilarUnitOutreach = (
  items: Pick<SimilarUnitOutreach, "sent_at">[] | undefined,
  now: Date = new Date()
): boolean =>
  (items ?? []).some((item) => {
    const sentAt = Date.parse(item.sent_at);
    if (Number.isNaN(sentAt)) return false;
    const age = now.getTime() - sentAt;
    return age >= 0 && age <= RECENT_WINDOW_MS;
  });

/** Outreach rows keyed by the thread message they produced. */
export const indexOutreachesByMessageId = (
  items: SimilarUnitOutreach[] | undefined
): Map<string, SimilarUnitOutreach> => {
  const result = new Map<string, SimilarUnitOutreach>();
  for (const item of items ?? [])
    if (item.message_id && !result.has(item.message_id))
      result.set(item.message_id, item);
  return result;
};

const formatNumber = (value: number) =>
  value.toLocaleString("en-US", { maximumFractionDigits: 1 });

const formatRent = (cents: number) => {
  const digits = cents % 100 === 0 ? 0 : 2;
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
};

const formatBeds = (beds: number) =>
  beds === 0 ? "Studio" : `${formatNumber(beds)} bd`;

// "1 bd / 1 ba, $1,650" — each piece only when its data is there.
const buildUnitDetails = ({
  beds,
  baths,
  rent_cents: rentCents
}: Pick<SimilarUnitOutreach, "beds" | "baths" | "rent_cents">):
  | string
  | null => {
  const size =
    beds === null
      ? null
      : [formatBeds(beds), baths === null ? null : `${formatNumber(baths)} ba`]
          .filter(Boolean)
          .join(" / ");
  const rent =
    rentCents === null || rentCents <= 0 ? null : formatRent(rentCents);
  const parts = [size, rent].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
};

const askedHome = (askedBeds: number | null) => {
  if (askedBeds === null) return "home";
  return askedBeds === 0 ? "studio" : `${formatNumber(askedBeds)}-bed`;
};

// "March 2026" → "March"; anything unexpected passes through as-is.
const askedMonthName = (askedMonth: string | null) =>
  askedMonth?.trim().split(/\s+/)[0] || null;

const quietSentence = (quietMonths: number | null) => {
  if (quietMonths === null || quietMonths <= 0) return null;
  return `Quiet for ${formatNumber(quietMonths)} ${quietMonths === 1 ? "month" : "months"}.`;
};

interface SimilarUnitContext {
  firstName: string;
  unitLabel: string;
  /** "(1 bd / 1 ba, $1,650)" without the parentheses; null drops it. */
  unitDetails: string | null;
  likeSentence: string;
  quietSentence: string | null;
}

/**
 * Pieces of the line above a part 2 message:
 * "A similar unit notification matched Ana to Unit 3B at Maple Court (1 bd / 1 ba,
 * $1,650). It's like the 1-bed Ana asked about in March. Quiet for 7 months."
 * Uses the contact's name rather than a pronoun.
 */
export const buildSimilarUnitContext = (
  item: SimilarUnitOutreach,
  contactFirstName: string
): SimilarUnitContext => {
  const firstName = contactFirstName.trim() || "this contact";
  const month = askedMonthName(item.asked_month);
  const asked = `It's like the ${askedHome(item.asked_beds)} ${firstName} asked about`;
  return {
    firstName,
    unitLabel: item.unit_label,
    unitDetails: buildUnitDetails(item),
    likeSentence: month ? `${asked} in ${month}.` : `${asked}.`,
    quietSentence: quietSentence(item.quiet_months)
  };
};

export const similarUnitContextText = (context: SimilarUnitContext): string =>
  [
    `A similar unit notification matched ${context.firstName} to ${context.unitLabel}${
      context.unitDetails ? ` (${context.unitDetails})` : ""
    }.`,
    context.likeSentence,
    context.quietSentence
  ]
    .filter(Boolean)
    .join(" ");

/** First name for the context line: the CRM contact's, else the thread name's first word. */
export const contactFirstNameFrom = (
  crmFirstName: string | null | undefined,
  contactName: string
): string => crmFirstName?.trim() || contactName.trim().split(/\s+/)[0] || "";

/** The outreach behind an outbound thread message, and whether it is part 2. */
export const resolveSimilarUnitMessage = (
  message: Pick<Message, "id" | "direction" | "source_label">,
  outreachByMessageId: Map<string, SimilarUnitOutreach>
): { outreach?: SimilarUnitOutreach; isSimilarUnitNotification: boolean } => {
  if (message.direction !== "outbound")
    return { isSimilarUnitNotification: false };
  const outreach = outreachByMessageId.get(message.id);
  return {
    outreach,
    isSimilarUnitNotification:
      Boolean(outreach) || isSimilarUnitNotificationMessage(message)
  };
};
